import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ModalDialogProvider } from "../../../components";
import { AllIcons, Toast } from "@telia/teddy";
import cn from "classnames";
import { connect, useDispatch, useSelector } from "react-redux";
import smoothscroll from "smoothscroll-polyfill";
import AppRoutes from "./Routes";
import { RootState } from "../../../../../models";
import { isRunningOnClientSide } from "../../../../../shared/util";
import AnnouncementText from "../../../checkout/components/atoms/AnnouncementText/AnnouncementText";
import ReSwitchAlert from "../../../checkout/components/atoms/ReSwitchAlert/ReSwitchAlert";
import DelayOnPurchaseModal from "../../../checkout/components/molecules/DelayOnPurchaseModal/DelayOnPurchaseModal";
import { getBookEndTime, getBookStartTime } from "../../../checkout/state/selectors/cartSelectors";
import { showDelayOnPurchaseModal as showDelayOnPurchaseModalSelector } from "../../../checkout/state/selectors/paymentSelectors";
import {
    ConnectedPageHeader,
    ConnectedPageFooter,
    selectors as NAV_SELECTORS,
    BookingWarning,
} from "../../../navigation";
import { pageLoaded } from "../../state/actions";
import { usePageViewTracker } from "../../hooks/usePageViewTracker";
import {
    getBackgroundColor,
    getContentfulFeatureToggles,
    getEnvironment,
    isCheckout as isCheckoutSelector,
    isRetailer as isRetailerSelector,
    showEmptiedOrderModal as showEmptiedOrderModalSelector,
    showInvalidOrderModal as showInvalidOrderModalSelector,
    showLockedOrderModal as showLockedOrderModalSelector,
    showNoneOrderModal as showNoneOrderModalSelector,
} from "../../state/selectors";

import CommandMenu from "../molecules/CommandMenu/CommandMenu";
import EmptiedOrderModal from "../molecules/EmptiedOrderModal/EmptiedOrderModal";
import InvalidOrderModal from "../molecules/InvalidOrderModal/InvalidOrderModal";
import LockedOrderModal from "../molecules/LockedOrderModal/LockedOrderModal";
import NoneOrderModal from "../molecules/NoneOrderModal/NoneOrderModal";
import "./style.less";

interface StateProps {
    backgroundColor: string;
    bookEndTime?: string;
    bookStartTime?: string;
    environment?: string;
    isCheckout: boolean;
    isRetailer: boolean;
    shouldHidePageFooter: boolean;
    shouldHidePageHeader: boolean;
    showDelayOnPurchaseModal: boolean;
    showEmptiedOrderModal: boolean;
    showInvalidOrderModal: boolean;
    showLockedOrderModal: boolean;
    showNoneOrderModal: boolean;
}

type AppRootProps = StateProps;

const queryClient = new QueryClient();

const AppRoot: React.FC<AppRootProps> = ({
    showLockedOrderModal,
    showInvalidOrderModal,
    showNoneOrderModal,
    showEmptiedOrderModal,
    showDelayOnPurchaseModal,
    shouldHidePageHeader,
    shouldHidePageFooter,
    bookStartTime,
    bookEndTime,
    backgroundColor = "inherit",
    isCheckout,
    isRetailer,
    environment,
}) => {
    const dispatch = useDispatch();
    const { minDelayInSeconds = 0, maxDelayInSeconds = 0 } =
        (useSelector((state: RootState) => getContentfulFeatureToggles(state).addDelayOnBuyButton) as unknown as
            | { minDelayInSeconds?: number; maxDelayInSeconds?: number }
            | undefined) ?? {};

    useEffect(() => {
        dispatch(pageLoaded(window.location.pathname));
        if (isRunningOnClientSide()) {
            smoothscroll.polyfill();
        }
    }, []);

    usePageViewTracker();

    const bgColor = isCheckout ? "white" : backgroundColor;

    return (
        <QueryClientProvider client={queryClient}>
            <ModalDialogProvider>
                {(environment !== "production" || process.env.NODE_ENV === "development") && <CommandMenu />}
                <AnnouncementText />
                <AllIcons />
                <Toast />
                {!shouldHidePageHeader && <ConnectedPageHeader isRetailer={isRetailer} />}
                {bookEndTime && <BookingWarning bookStartTime={bookStartTime} bookEndTime={bookEndTime} />}
                {!shouldHidePageHeader && <ReSwitchAlert />}
                <main
                    className={cn("app-root__main-content", {
                        [`app-root--${bgColor}`]: true,
                        "app-root--retailer": isRetailer,
                    })}
                >
                    <AppRoutes />
                </main>
                {showLockedOrderModal && <LockedOrderModal />}
                {showInvalidOrderModal && <InvalidOrderModal />}
                {showNoneOrderModal && <NoneOrderModal />}
                {showEmptiedOrderModal && <EmptiedOrderModal />}
                {!shouldHidePageFooter && <ConnectedPageFooter />}
                <DelayOnPurchaseModal
                    open={showDelayOnPurchaseModal}
                    minDelayInSeconds={minDelayInSeconds || 0}
                    maxDelayInSeconds={maxDelayInSeconds || 0}
                />
            </ModalDialogProvider>
        </QueryClientProvider>
    );
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    showInvalidOrderModal: showInvalidOrderModalSelector(state),
    showLockedOrderModal: showLockedOrderModalSelector(state),
    showNoneOrderModal: showNoneOrderModalSelector(state),
    showEmptiedOrderModal: showEmptiedOrderModalSelector(state),
    showDelayOnPurchaseModal: showDelayOnPurchaseModalSelector(state),
    shouldHidePageHeader: NAV_SELECTORS.shouldHidePageHeader(state),
    shouldHidePageFooter: NAV_SELECTORS.shouldHidePageFooter(state),
    bookStartTime: getBookStartTime(state),
    bookEndTime: getBookEndTime(state),
    isCheckout: isCheckoutSelector(state),
    backgroundColor: getBackgroundColor(state),
    isRetailer: isRetailerSelector(state),
    environment: getEnvironment(state),
});

export default connect(mapStateToProps)(AppRoot);
